'use client';

import { useState, useEffect, useRef } from 'react';
const CustomCalendar = ({
  itemId,
  itemName,
  scheduledDates,
  setScheduledDates,
  saveSchedule,
  formatDateKey,
  isPastDate,
  today,
  loading,
  getScheduledDatesCount,
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const firstDayWeekday = firstDayOfMonth.getDay();

  const daysInMonth = lastDayOfMonth.getDate();
  const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const emptyDays = Array.from({ length: firstDayWeekday }, (_, i) => null);

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const navigateMonth = direction => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
    console.log('Navigating to:', newDate.getFullYear(), newDate.getMonth() + 1);
  };

  const getDayStatus = (itemId, date) => {
    const dateKey = formatDateKey(date);
    const schedule = scheduledDates[itemId];

    // Default to ON if no schedule is set
    if (!schedule || !(dateKey in schedule)) {
      return true; // Default to ON
    }
    return schedule[dateKey];
  };

  // Get week number for a given date in the current viewing month
  const getWeekOfMonth = date => {
    // Ensure we're working with the current viewing month/year
    const viewingDate = new Date(year, month, date.getDate());
    const firstDay = new Date(year, month, 1);
    const firstDayWeekday = firstDay.getDay();
    return Math.ceil((viewingDate.getDate() + firstDayWeekday) / 7);
  };

  // Get all dates in a specific week of the current viewing month
  const getDatesInWeek = weekNumber => {
    const dates = [];
    const firstDay = new Date(year, month, 1);
    const firstDayWeekday = firstDay.getDay();

    const startDate = (weekNumber - 1) * 7 - firstDayWeekday + 1;
    const endDate = Math.min(startDate + 6, daysInMonth);

    for (let day = Math.max(1, startDate); day <= endDate; day++) {
      dates.push(new Date(year, month, day));
    }

    return dates;
  };

  // Toggle entire week - FIXED VERSION
  const toggleWeek = async weekNumber => {
    console.log(`Toggling week ${weekNumber} in ${monthNames[month]} ${year}`);

    // Store current viewing date before any operations
    const currentViewingDate = new Date(currentDate);

    const weekDates = getDatesInWeek(weekNumber);
    const futureDates = weekDates.filter(date => !isPastDate(date));

    if (futureDates.length === 0) {
      console.log('No future dates in this week');
      return; // No future dates in this week
    }

    // Check if all future dates in the week are ON
    const allOn = futureDates.every(date => getDayStatus(itemId, date));
    const newStatus = !allOn; // If all are ON, turn them OFF, otherwise turn them ON

    console.log(`Setting week ${weekNumber} to ${newStatus ? 'ON' : 'OFF'}`);

    const newSchedule = { ...scheduledDates[itemId] };

    futureDates.forEach(date => {
      const dateKey = formatDateKey(date);
      newSchedule[dateKey] = newStatus;
      console.log(`Setting ${dateKey} to ${newStatus}`);
    });

    // Update local state first
    setScheduledDates(prev => ({
      ...prev,
      [itemId]: newSchedule,
    }));

    // Save to database
    await saveSchedule(newSchedule);

    if (currentDate.getTime() !== currentViewingDate.getTime()) {
      console.log('Restoring viewing date:', currentViewingDate);
      setCurrentDate(currentViewingDate);
    }

    // Ensure we stay on the same month after the operation
    setTimeout(() => {}, 100);
  };

  const handleDayClick = async (date, event) => {
    event.stopPropagation();
    // const bb = new Intl.DateTimeFormat('en-CA').format(date)

    const dateKey = formatDateKey(date);

    console.log('formatDateKey', dateKey);

    const dateOnly = new Date(date);
    dateOnly.setHours(0, 0, 0, 0);

    if (dateOnly < today) return;

    // Store current viewing date
    const currentViewingDate = new Date(currentDate);

    const currentStatus = getDayStatus(itemId, date);
    const newStatus = !currentStatus;

    const newSchedule = {
      ...scheduledDates[itemId],
      [dateKey]: newStatus,
    };

    setScheduledDates(prev => ({
      ...prev,
      [itemId]: newSchedule,
    }));

    await saveSchedule(newSchedule);

    // Ensure we stay on the same month after the operation
    setTimeout(() => {
      if (currentDate.getTime() !== currentViewingDate.getTime()) {
        setCurrentDate(currentViewingDate);
      }
    }, 100);
  };

  const getDayClasses = day => {
    const date = new Date(year, month, day);
    const isOn = getDayStatus(itemId, date);
    const isPast = isPastDate(date);
    const isToday = formatDateKey(date) === formatDateKey(today);

    let classes = 'calendar-day ';

    if (isPast) {
      classes += isOn ? 'past-on' : 'past-off';
    } else {
      classes += isOn ? 'future-on' : 'future-off';
    }

    if (isToday) {
      classes += ' today';
    }

    return classes;
  };

  // Get unique weeks in the current month
  const getWeeksInMonth = () => {
    const weeks = new Set();
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      weeks.add(getWeekOfMonth(date));
    }
    return Array.from(weeks).sort();
  };

  return (
    <div className="calendar-container" onClick={e => e.stopPropagation()}>
      {loading && (
        <div className="loading-overlay">
          <div>Saving...</div>
        </div>
      )}

      {/* Debug info - remove in production */}
      <div style={{ fontSize: '10px', color: '#999', marginBottom: '5px' }}>
        Viewing: {monthNames[month]} {year}
      </div>

      {/* Calendar Header */}
      <div className="calendar-header">
        <button
          type="button"
          className="nav-btn"
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            navigateMonth(-1);
          }}
        >
          <span className="nav-arrow">‹</span>
        </button>
        <h3 className="month-title">
          {monthNames[month]} {year}
        </h3>
        <button
          type="button"
          className="nav-btn"
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            navigateMonth(1);
          }}
        >
          <span className="nav-arrow">›</span>
        </button>
      </div>

      {/* Week Toggle Buttons */}
      <div className="week-controls">
        <div className="week-controls-title">Toggle Entire Week:</div>
        <div className="week-buttons">
          {getWeeksInMonth().map(weekNum => {
            const weekDates = getDatesInWeek(weekNum);
            const futureDates = weekDates.filter(date => !isPastDate(date));
            const hasActiveDates = futureDates.length > 0;
            const allOn = futureDates.every(date => getDayStatus(itemId, date));

            return (
              <button
                key={`${year}-${month}-week-${weekNum}`} // More specific key
                type="button"
                className={`week-btn ${!hasActiveDates ? 'disabled' : allOn ? 'week-on' : 'week-off'}`}
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleWeek(weekNum);
                }}
                disabled={!hasActiveDates}
                title={`Week ${weekNum} - Click to toggle all future dates in this week`}
              >
                Week {weekNum}
              </button>
            );
          })}
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="calendar-grid">
        {/* Week day headers */}
        {weekDays.map(day => (
          <div key={day} className="weekday-header">
            {day}
          </div>
        ))}

        {/* Empty cells for days before month starts */}
        {emptyDays.map((_, index) => (
          <div key={`empty-${index}`} className="empty-day"></div>
        ))}

        {/* Calendar days */}
        {daysArray.map(day => {
          const date = new Date(year, month, day);
          return (
            <div
              key={`${year}-${month}-${day}`} // More specific key
              className={getDayClasses(day)}
              onClick={event => handleDayClick(date, event)}
              title={`${formatDateKey(date)} - ${getDayStatus(itemId, date) ? 'ON' : 'OFF'}${isPastDate(date) ? ' (Past - Cannot Edit)' : ''}`}
            >
              {day}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="calendar-legend">
        <div className="legend-item">
          <div className="legend-color future-on-legend"></div>
          <span>ON</span>
        </div>
        <div className="legend-item">
          <div className="legend-color future-off-legend"></div>
          <span>OFF</span>
        </div>
        <div className="legend-item">
          <div className="legend-color past-on-legend"></div>
          <span>Past ON</span>
        </div>
        <div className="legend-item">
          <div className="legend-color past-off-legend"></div>
          <span>Past OFF</span>
        </div>
      </div>

      {/* Status Summary */}
      <div className="calendar-summary">
        <p>Click on future dates to toggle between ON/OFF</p>
        <p>Use week buttons to toggle entire weeks at once</p>
        <p>Past days show historical ON/OFF status but cannot be changed</p>
        {/* <p>Total ON days: {getScheduledDatesCount(itemId)}</p> */}
        <p>
          Total On Days in current month:{' '}
          {
            daysArray.filter(day => {
              const date = new Date(year, month, day);
              return getDayStatus(itemId, date) === true;
            }).length
          }
        </p>
      </div>
    </div>
  );
};

export default CustomCalendar;
